import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, Sparkles, LogOut, Clock } from 'lucide-react';
import { toast } from 'sonner';
import AudioCard from '@/components/AudioCard';
import VoiceWave from '@/components/VoiceWave';
import WaveLoader from '@/components/WaveLoader';
import InnerShiftCard from '@/components/InnerShiftCard';
import PremiumChallengesUpsell from '@/components/PremiumChallengesUpsell';
import { getTracks, getFavorites, getListeningStats } from '@/lib/api';
import { startUpgrade } from '@/lib/billing';
import { useAuth } from '@/contexts/AuthContext';

export default function ConfidenceLibraryPage() {
  const [tracks, setTracks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [stats, setStats] = useState({ total_minutes: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user === false) {
      navigate('/confidence');
      return;
    }
    if (!user) return;
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [tracksRes, favRes, statsRes] = await Promise.all([
        getTracks('confidence'),
        getFavorites().catch(() => ({ data: [] })),
        getListeningStats().catch(() => ({ data: { total_minutes: 0 } })),
      ]);
      // Defensive filter — only confidence tracks
      const conf = (tracksRes.data || []).filter(
        t => (t.category === 'confidence') || (Array.isArray(t.categories) && t.categories.includes('confidence'))
      );
      setTracks(conf);
      setFavorites((favRes.data || []).map(t => t.id));
      setStats(statsRes.data || { total_minutes: 0 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load tracks');
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteChange = (trackId, isFavorite) => {
    setFavorites(isFavorite ? [...favorites, trackId] : favorites.filter(id => id !== trackId));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/confidence');
  };

  if (loading || !user) {
    return <WaveLoader label="Loading your tracks" />;
  }

  const greetName = (user.name || user.email?.split('@')[0] || 'friend').split(' ')[0];

  return (
    <div className="min-h-screen bg-zinc-950 pb-16" data-testid="confidence-library-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-zinc-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl py-4 flex items-center justify-between">
          <Link to="/confidence" className="flex items-center gap-3">
            <VoiceWave size="sm" speed={1.2} lineCount={4} />
            <span className="text-sm tracking-widest text-zinc-200 uppercase">Lifetunes · Confidence</span>
          </Link>
          <button
            onClick={handleLogout}
            className="text-xs text-zinc-500 hover:text-zinc-200 flex items-center gap-1.5 transition-colors"
            data-testid="confidence-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 max-w-5xl pt-10">
        {/* Greeting + mini stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="md:col-span-2 p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-zinc-900/60 border border-amber-500/15">
            <p className="text-xs text-amber-300 tracking-widest uppercase mb-2">Welcome back</p>
            <h1 className="text-2xl md:text-3xl font-serif text-white capitalize">
              Hey {greetName}, ready to build your confidence?
            </h1>
            <p className="text-sm text-zinc-400 mt-2">
              Pick a track below and press play. Your progress is saved automatically.
            </p>
          </div>
          <div className="p-5 rounded-2xl bg-zinc-900/60 border border-white/5 flex flex-col justify-center">
            <Clock className="w-4 h-4 text-violet-400 mb-2" />
            <p className="text-3xl font-serif text-white">{stats.total_minutes || 0}</p>
            <p className="text-xs text-zinc-500">minutes listened</p>
          </div>
        </div>

        {/* 21-Day Inner Shift (full card) */}
        <InnerShiftCard variant="embedded" />

        {/* Premium 14 & 21-day challenges upsell — show for users who are not yet premium */}
        {!user?.is_premium && <PremiumChallengesUpsell />}

        {/* Upgrade CTA */}
        <div className="mb-10 p-5 rounded-2xl bg-gradient-to-r from-violet-500/15 via-zinc-900/40 to-amber-500/10 border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">Unlock the full library</p>
              <p className="text-xs text-zinc-400 mt-1">
                Sleep, Wealth, Healing, Focus & more — start your $1 / 7-day trial. $7/month after.
              </p>
            </div>
          </div>
          <button
            onClick={() => startUpgrade()}
            className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-100 font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shrink-0"
            data-testid="confidence-upgrade-btn"
          >
            Upgrade — $1 trial
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Tracks grid */}
        <section data-testid="confidence-tracks">
          <h2 className="text-xl font-serif text-white mb-1">Confidence Tracks</h2>
          <p className="text-sm text-zinc-500 mb-6">{tracks.length} {tracks.length === 1 ? 'track' : 'tracks'} available</p>

          {tracks.length === 0 ? (
            <div className="text-center py-16 rounded-2xl bg-zinc-900/40 border border-dashed border-white/5">
              <p className="text-zinc-500 text-sm">No confidence tracks available yet. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {tracks.map(track => (
                <AudioCard
                  key={track.id}
                  track={track}
                  isFavorite={favorites.includes(track.id)}
                  onFavoriteChange={handleFavoriteChange}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
